import type { EpisodeVisualSource, EpisodeVisualSourceParsers } from '@/_contract/episodeVisualSource';

function record(value: unknown): Record<string, unknown> {
    if (!value || typeof value !== 'object' || Array.isArray(value)) throw new Error('Invalid episode visual source object.');
    return value as Record<string, unknown>;
}

export function parseEpisodeVisualSources<TTrace extends object, TArrangement>(
    value: unknown, parsers: EpisodeVisualSourceParsers<TTrace, TArrangement>,
): ReadonlyMap<string, EpisodeVisualSource<TTrace, TArrangement>> {
    if (!Array.isArray(value)) throw new Error('Episode visual sources require an array.');
    const sources = new Map<string, EpisodeVisualSource<TTrace, TArrangement>>();
    for (const entry of value) {
        const { id, schemaVersion, sourceKind, ...payload } = record(entry);
        if (typeof id !== 'string' || !/^[a-z][a-z0-9-]*$/.test(id)) {
            throw new Error(`Invalid episode visual source id '${String(id)}'.`);
        }
        if (sources.has(id)) throw new Error(`Duplicate episode visual source '${id}'.`);
        if (schemaVersion !== 1) throw new Error('Unsupported episode visual source schema version.');
        if (sourceKind === 'trace') {
            sources.set(id, { ...parsers.parseTrace(payload), id, schemaVersion: 1, sourceKind });
        } else if (sourceKind === 'concept') {
            for (const key of Object.keys(payload)) {
                if (key !== 'arrangement') throw new Error(`Unknown concept visual source field '${key}'.`);
            }
            sources.set(id, { id, schemaVersion: 1, sourceKind, arrangement: parsers.parseArrangement(payload.arrangement) });
        } else {
            throw new Error('Unknown episode visual source kind.');
        }
    }
    return sources;
}

/** Resolve a single allowed slot; callers choose the slot and validate the resulting domain payload. */
export function resolveEpisodeVisualSource<TTrace extends object, TArrangement>(
    value: Record<string, unknown>, sources: ReadonlyMap<string, EpisodeVisualSource<TTrace, TArrangement>> | undefined,
    kind: 'trace' | 'concept', payloadFields: readonly string[],
): Record<string, unknown> {
    if (value.visualSource === undefined) return value;
    for (const field of payloadFields) {
        if (value[field] !== undefined) throw new Error(`Episode visualSource cannot also declare '${field}'.`);
    }
    const id = value.visualSource;
    if (typeof id !== 'string' || !id.trim()) throw new Error('Episode visualSource requires nonempty text.');
    const source = sources?.get(id);
    if (!source) throw new Error(`Episode visual source '${id}' is unavailable.`);
    if (source.sourceKind !== kind) throw new Error(`Episode visual source '${id}' requires a ${source.sourceKind} slot.`);
    const resolved: Record<string, unknown> = { ...value };
    for (const [key, payload] of Object.entries(source)) {
        if (key === 'id' || key === 'schemaVersion' || key === 'sourceKind') continue;
        if (!payloadFields.includes(key)) throw new Error(`Unexpected episode visual source payload field '${key}'.`);
        resolved[key] = payload;
    }
    delete resolved.visualSource;
    return resolved;
}
